import { ErrorModal } from "@/components/shared/modal/error-modal";
import { PageTitle } from "@/components/shared/title/page-title";
import { humanSeconds } from "@/components/shared/xml-source-status/status";
import { useHasPermission } from "@/hooks/permissions/use-has-permission";
import {
	PurgeImpact,
	RETENTION,
	SILENCE,
	THRESHOLD,
	getPurgeImpact,
	getXmlSettings,
	setXmlSetting,
} from "@/repositories/xml-settings/xml-settings.repository";
import { ShortDateTimeFormat } from "@/utils/date-time.format";
import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	Paper,
	TextField,
	Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import React, { useEffect, useState } from "react";

// DetectSilentXmlServiceJob polls once a minute, so anything under 60 s cannot be
// honoured. Kept in sync with BrokerController.
const MIN_SILENCE_SECONDS = 60;

const useStyles = makeStyles(() => ({
	page: { padding: 12, maxWidth: 720 },
	row: { padding: "14px 16px", marginBottom: 10 },
	name: { fontWeight: 600 },
	hint: { color: "#666", fontSize: 12, marginTop: 4 },
	warn: { color: "#c62828", fontSize: 12, marginTop: 4 },
	input: { width: 120 },
	unit: { color: "#666", fontSize: 13, marginLeft: 8 },
}));

type Draft = Record<string, string>;

export const XmlSettingsPage = () => {
	const classes = useStyles();
	const canRead = useHasPermission("Broker.GetSettings");
	const canWrite = useHasPermission("Broker.SetSetting");

	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [rejected, setRejected] = useState<string | null>(null);
	const [failure, setFailure] = useState<string | null>(null);
	const [justSaved, setJustSaved] = useState(false);
	const [stored, setStored] = useState<Draft>({});
	const [draft, setDraft] = useState<Draft>({});
	const [loopSeconds, setLoopSeconds] = useState<number | null>(null);
	const [impact, setImpact] = useState<PurgeImpact | null>(null);

	const load = async () => {
		setLoading(true);
		const resp = await getXmlSettings();
		if (!resp.ok || !resp.data) {
			setFailure(resp.message || "Could not load the settings.");
			setLoading(false);
			return;
		}

		const values: Draft = {};
		for (const s of resp.data.settings) values[s.settingName] = s.value ?? "";
		setStored(values);
		setDraft(values);
		setLoopSeconds(resp.data.loopSeconds);
		setFailure(null);
		setLoading(false);
	};

	useEffect(() => {
		if (canRead) load();
		else setLoading(false);
	}, [canRead]);

	const num = (name: string) => Number.parseInt(draft[name] ?? "", 10);
	const changed = (name: string) =>
		(draft[name] ?? "") !== (stored[name] ?? "");

	// The same rules the API enforces, repeated here only so the answer is immediate.
	// The API is the one that decides.
	const problem = (name: string): string | null => {
		const v = num(name);
		if (Number.isNaN(v)) return "Must be a whole number";

		if (name === THRESHOLD) {
			return v < 1 ? "Must be 1 or greater" : null;
		}
		if (name === RETENTION) {
			return v < 1 ? "Must be 1 day or greater" : null;
		}
		if (name === SILENCE) {
			if (v < MIN_SILENCE_SECONDS) {
				return `Must be at least ${MIN_SILENCE_SECONDS} s, how often the detector runs`;
			}
			if (loopSeconds && v < loopSeconds * 3) {
				return `Must be at least ${
					loopSeconds * 3
				} s, three times the ${loopSeconds} s loop`;
			}
		}
		return null;
	};

	const anyProblem = [THRESHOLD, SILENCE, RETENTION].some((n) => problem(n));
	const anyChange = [THRESHOLD, SILENCE, RETENTION].some(changed);

	const write = async () => {
		setSaving(true);
		for (const name of [THRESHOLD, SILENCE, RETENTION]) {
			if (!changed(name)) continue;
			const resp = await setXmlSetting(name, draft[name]);
			if (!resp.ok) {
				const message = resp.message || `Could not save ${name}.`;
				if (resp.rejected) setRejected(message);
				else setFailure(message);
				setSaving(false);
				setImpact(null);
				return;
			}
		}
		setImpact(null);
		setSaving(false);
		setJustSaved(true);
		await load();
	};

	const save = async () => {
		setRejected(null);
		setJustSaved(false);

		// Retention is the only setting here that destroys data, and only when it
		// goes down. The count comes from the same cutoff the job will use.
		const lowered =
			changed(RETENTION) &&
			num(RETENTION) < Number.parseInt(stored[RETENTION], 10);

		if (lowered) {
			setSaving(true);
			const resp = await getPurgeImpact(num(RETENTION));
			setSaving(false);
			if (!resp.ok) {
				setFailure(resp.message || "Could not check what would be deleted.");
				return;
			}
			setImpact(resp.data ?? ({ rowsToDelete: 0 } as PurgeImpact));
			return;
		}

		await write();
	};

	if (!canRead) {
		return (
			<>
				<PageTitle title="XML Feed Settings" />
				<div className="p-4 text-muted">
					You do not have permission to view the XML feed settings.
				</div>
			</>
		);
	}

	if (loading) {
		return (
			<>
				<PageTitle title="XML Feed Settings" />
				<div className="p-4">
					<CircularProgress size={22} />
				</div>
			</>
		);
	}

	const field = (
		name: string,
		label: string,
		unit: string,
		hint: React.ReactNode,
		warn?: React.ReactNode,
	) => {
		const bad = problem(name);
		return (
			<Paper className={classes.row} key={name} variant="outlined">
				<Grid container alignItems="center" justifyContent="space-between">
					<Grid item xs={7}>
						<div className={classes.name}>{label}</div>
						<div className={classes.hint}>{hint}</div>
						{warn && <div className={classes.warn}>{warn}</div>}
					</Grid>
					<Grid item>
						<TextField
							className={classes.input}
							size="small"
							variant="outlined"
							type="number"
							disabled={!canWrite || saving}
							value={draft[name] ?? ""}
							error={!!bad}
							helperText={bad ?? " "}
							onChange={(e) => setDraft({ ...draft, [name]: e.target.value })}
						/>
						<span className={classes.unit}>{unit}</span>
					</Grid>
				</Grid>
			</Paper>
		);
	};

	const thresholdHint = loopSeconds
		? `Failed folder reads in a row before a source turns red. ${
				num(THRESHOLD) || 0
		  } checks is ${humanSeconds(
				(num(THRESHOLD) || 0) * loopSeconds,
		  )} at the ${loopSeconds} s loop the feed reports.`
		: "Failed folder reads in a row before a source turns red. No feed has reported its loop interval yet, so this cannot be shown in time.";

	return (
		<>
			<PageTitle title="XML Feed Settings" />
			<ErrorModal error={failure} onHide={() => setFailure(null)} />
			<div className={classes.page}>
				{field(THRESHOLD, "Failure threshold", "checks", thresholdHint)}
				{field(
					SILENCE,
					"Silence threshold",
					"seconds",
					"Time without a heartbeat before a source is marked as not reporting.",
				)}
				{field(
					RETENTION,
					"Message retention",
					"days",
					"Days of received XML messages kept in the archive.",
					"Lowering this deletes archived messages on the next purge.",
				)}

				{rejected && (
					<Typography color="error" style={{ marginTop: 8 }}>
						{rejected}
					</Typography>
				)}
				{justSaved && !rejected && (
					<Typography style={{ color: "#2e7d32", marginTop: 8 }}>
						Saved.
					</Typography>
				)}

				{!canWrite && (
					<div className={classes.hint}>
						You can view these values but not change them.
					</div>
				)}

				<Button
					variant="contained"
					color="primary"
					style={{ marginTop: 10 }}
					disabled={!canWrite || saving || !anyChange || anyProblem}
					onClick={save}
				>
					{saving ? <CircularProgress size={18} /> : "Save"}
				</Button>
			</div>

			<Dialog
				open={!!impact}
				onClose={() => setImpact(null)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>Delete archived messages?</DialogTitle>
				<DialogContent>
					{impact && impact.rowsToDelete > 0 ? (
						<>
							<Typography>
								Keeping {num(RETENTION)} days deletes{" "}
								<b>{impact.rowsToDelete.toLocaleString()}</b> archived messages
								on the next purge.
							</Typography>
							<Typography style={{ marginTop: 8 }}>
								Everything received from{" "}
								<b>{ShortDateTimeFormat(impact.oldestAffected)}</b> to{" "}
								<b>{ShortDateTimeFormat(impact.newestAffected)}</b>.
							</Typography>
							<Typography style={{ marginTop: 8, color: "#c62828" }}>
								This cannot be undone.
							</Typography>
						</>
					) : (
						<Typography>
							Keeping {num(RETENTION)} days deletes nothing right now: no
							archived message is older than that yet.
						</Typography>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setImpact(null)} disabled={saving}>
						Cancel
					</Button>
					<Button
						onClick={write}
						color="secondary"
						variant="contained"
						disabled={saving}
					>
						{saving ? <CircularProgress size={18} /> : "Save anyway"}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
